import React, { useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const CruciblePage = () => {
  // Performance tracking for Core Web Vitals
  useEffect(() => {
    // Report Core Web Vitals
    if ('web-vital' in window) {
      window.webVitals?.getCLS(console.log);
      window.webVitals?.getFID(console.log);
      window.webVitals?.getFCP(console.log);
      window.webVitals?.getLCP(console.log);
      window.webVitals?.getTTFB(console.log);
    }

    // Handle anchor scrolling for direct links
    const handleAnchorScroll = () => {
      const hash = window.location.hash;
      if (hash) {
        // Wait for component to fully render
        setTimeout(() => {
          const element = document.querySelector(hash);
          if (element) {
            element.scrollIntoView({ 
              behavior: 'smooth',
              block: 'start'
            });
          }
        }, 100);
      }
    };

    // Handle anchor on initial load
    handleAnchorScroll();

    // Handle anchor on hash change (for same-page navigation)
    window.addEventListener('hashchange', handleAnchorScroll);

    // Enhanced focus management for accessibility
    const handleKeyDown = (e) => {
      // Skip to main content on keyboard navigation
      if (e.ctrlKey || e.metaKey) return;
      
      // Improve focus management for CTA buttons and interactive elements
      if (e.code === 'Space' || e.code === 'Enter') {
        const target = e.target;
        if (target.getAttribute('role') === 'button' && !target.href) {
          e.preventDefault();
          target.click();
        }
      }
      
      // ESC key to close any open modals or dropdowns
      if (e.code === 'Escape') {
        const focusedElement = document.activeElement;
        focusedElement?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('hashchange', handleAnchorScroll);
    };
  }, []);

  // Enhanced structured data for SEO matching EMBER branding
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "The Crucible: Peer Leadership Accelerator™",
    "description": "Pilot cohort 8-week peer leadership program at $697. Develop Emotional Intelligence, Personal Agency & Authentic Connection with fellow leaders. Limited-time pilot pricing.",
    "provider": {
      "@type": "Organization",
      "name": "EMBER Impact Ventures LLC",
      "url": "https://emberimpact.com",
      "logo": "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png",
      "sameAs": [
        "https://www.linkedin.com/company/ember-impact-ventures",
        "https://twitter.com/EmberLeadership"
      ]
    },
    "courseMode": "online",
    "duration": "P8W", 
    "startDate": "2025-10-18",
    "endDate": "2025-12-13",
    "schedule": "Weekly Sessions",
    "educationalLevel": "Executive",
    "audience": {
      "@type": "EducationalAudience",
      "audienceType": "Leadership Development"
    },
    "offers": {
      "@type": "Offer",
      "price": "697",
      "priceCurrency": "USD",
      "availability": "https://schema.org/LimitedAvailability",
      "validFrom": "2025-10-11",
      "validThrough": "2025-10-11"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "47"
    },
    "applicationDeadline": "2025-10-11",
    "maximumAttendeeCapacity": 12,
    "remainingAttendeeCapacity": 12,
    "instructor": {
      "@type": "Person",
      "name": "Matthew Callison, PhD",
      "description": "Expert in creating psychological safety and facilitating breakthrough conversations among leaders"
    }
  };

  return (
    <>
      <SEOHead 
        title="The Crucible Peer Leadership Accelerator™ | EMBER"
        description="Pilot cohort leadership development program at $697. Weekly cohort-based sessions building emotional intelligence, personal agency, and authentic connection through fire-tested frameworks."
        keywords="leadership development, leadership training, authentic leadership, executive coaching, leadership transformation, peer accelerator, leadership community, professional development, leadership skills, peer learning, 8-week program, emotional intelligence, personal agency"
        ogImage="https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png"
        canonicalUrl="/crucible"
        structuredData={structuredData}
      />
      
      {/* Preconnect for performance optimization */}
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
      <link rel="preconnect" href="https://form.typeform.com" />
      <link rel="preconnect" href="https://calendly.com" />
      <link rel="preconnect" href="https://lh3.googleusercontent.com" />
      
      {/* Enhanced CSS with mobile-first design and WCAG compliance */}
      <style>{`
        :root {
            /* EMBER Brand Colors - Consistent with site */
            --spark: #FFC107;
            --glow: #FF9800;
            --ember-fire: #FF5722;
            --ember-flame: #E91E63;
            --ember-heat: #9C27B0;
            --depth: #673AB7;
            
            /* Steel Colors */
            --steel-light: #B0BEC5;
            --steel-medium: #78909C;
            --steel-dark: #455A64;
            --steel-charcoal: #263238;
            
            /* Neutrals */
            --charcoal: #212121;
            --warm: #FFF8F0;
            --neutral-light: #F5F5F5;
            --neutral-medium: #757575;
            
            /* Brand Gradients */
            --ember-gradient: linear-gradient(135deg, #FFC107 0%, #FF9800 25%, #FF5722 50%, #E91E63 75%, #9C27B0 100%);
            --flame-gradient: linear-gradient(135deg, #E91E63 0%, #9C27B0 50%, #673AB7 100%);
            --forge-gradient: linear-gradient(135deg, #FF5722 0%, #E91E63 25%, #9C27B0 50%, #673AB7 75%, #455A64 100%);
            
            /* WCAG AA Compliant Colors */
            --text-primary: #212121; /* 4.5:1 contrast ratio */
            --text-secondary: #455A64; /* 4.5:1 contrast ratio */
            --link-color: #FF5722; /* 4.51:1 contrast ratio */
            --link-hover: #E64A19; /* 5.2:1 contrast ratio */
        }

        /* Reset and base styles */
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        /* Mobile-first body styles */
        body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
            line-height: 1.6;
            color: var(--text-primary);
            background: var(--warm);
            font-size: 16px; /* Base font size for accessibility */
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
        }

        /* Container - Mobile first */
        .container {
            max-width: 100%;
            margin: 0 auto;
            padding: 0 1rem; /* 16px minimum touch target */
        }

        /* Skip Navigation - WCAG AA */
        .skip-nav {
            position: absolute;
            top: -40px;
            left: 6px;
            background: var(--ember-fire);
            color: white;
            padding: 8px 16px;
            text-decoration: none;
            border-radius: 0 0 4px 4px;
            z-index: 9999;
            font-weight: 600;
            font-size: 14px;
            transition: top 0.3s ease;
        }

        .skip-nav:focus {
            top: 0;
            outline: 3px solid #FFC107;
            outline-offset: 2px;
        }

        /* Enhanced focus styles for WCAG AAA */
        *:focus {
            outline: 3px solid #FFC107;
            outline-offset: 2px;
            border-radius: 2px;
        }

        /* Button styles - Mobile first with 44px minimum touch target */
        .cta-button {
            background: var(--ember-fire);
            color: white;
            padding: 12px 24px; /* Increased padding for mobile */
            border: none;
            border-radius: 6px;
            font-weight: 600;
            text-decoration: none;
            display: inline-block;
            transition: all 0.3s ease;
            min-height: 44px; /* WCAG minimum touch target */
            min-width: 44px;
            text-align: center;
            font-size: 16px; /* Minimum readable size */
            cursor: pointer;
            line-height: 1.2;
        }

        .cta-button:hover,
        .cta-button:focus {
            background: var(--link-hover);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(255, 87, 34, 0.3);
        }

        .cta-button:active {
            transform: translateY(0);
        }

        /* Container and layout styles */

        .hero::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: var(--ember-gradient);
            opacity: 0.05;
            z-index: 1;
        }

        .hero .container {
            position: relative;
            z-index: 2;
        }

        /* Mobile-first typography */
        .hero h1 {
            font-size: 1.75rem; /* 28px - readable on mobile */
            font-weight: 800;
            margin-bottom: 1rem;
            line-height: 1.2;
            color: var(--text-primary);
        }

        .hero h1 .highlight {
            color: var(--ember-fire);
        }

        .hero-subtitle {
            font-size: 1rem; /* 16px base size */
            font-weight: 400;
            max-width: 100%;
            line-height: 1.5;
            margin: 0 auto 2rem auto;
            color: var(--text-secondary);
        }

        /* Cohort details - Mobile first grid */
        .cohort-details {
            background: white;
            border: 2px solid var(--ember-fire);
            padding: 1rem; /* Reduced for mobile */
            margin: 1.5rem auto;
            border-radius: 8px;
            display: grid;
            grid-template-columns: 1fr; /* Single column on mobile */
            gap: 1rem;
            max-width: 100%;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .cohort-detail {
            text-align: center;
        }

        .cohort-detail .label {
            font-size: 0.875rem; /* 14px */
            color: var(--text-secondary);
            font-weight: 500;
            margin-bottom: 0.5rem;
        }

        .cohort-detail .value {
            font-size: 1.125rem; /* 18px */
            font-weight: 700;
            color: var(--ember-fire);
        }

        .urgency-banner {
            background: var(--flame-gradient);
            color: white;
            padding: 1rem 1.5rem;
            border-radius: 6px;
            text-align: center;
            margin: 1.5rem auto;
            font-weight: 600;
            max-width: 600px; /* Limit width instead of full width */
            font-size: 0.875rem; /* Smaller on mobile */
            display: inline-block;
        }

        /* Section Styles - Mobile First */
        section {
            padding: 2rem 0; /* Reduced for mobile */
        }

        .section-title {
            font-size: 1.5rem; /* 24px - readable on mobile */
            font-weight: 700;
            text-align: center;
            margin-bottom: 1rem;
            color: var(--text-primary);
            line-height: 1.3;
        }

        .section-subtitle {
            font-size: 1rem; /* 16px base */
            color: var(--text-secondary);
            text-align: center;
            margin-bottom: 2rem;
            max-width: 100%;
            margin-left: auto;
            margin-right: auto;
            line-height: 1.5;
        }

        /* Competencies - Mobile First Grid */
        .competencies {
            background: var(--neutral-light);
            border-top: 4px solid var(--steel-light);
        }

        .competencies-grid {
            display: grid;
            grid-template-columns: 1fr; /* Single column on mobile */
            gap: 1rem;
            margin-top: 1.5rem;
        }

        .competency-card {
            background: white;
            padding: 1.5rem;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
            border-left: 4px solid var(--ember-fire);
            border-top: 1px solid var(--steel-light);
            text-align: left;
        }

        .competency-title {
            font-size: 1.125rem; /* 18px */
            font-weight: 700;
            margin-bottom: 1rem;
            color: var(--text-primary);
            line-height: 1.3;
        }

        .competency-description {
            color: var(--text-secondary);
            margin-bottom: 1rem;
            line-height: 1.6;
            font-size: 0.9rem; /* 14px */
        }

        .competency-outcomes {
            color: var(--text-primary);
            font-size: 0.875rem; /* 14px */
            line-height: 1.5;
        }

        /* Leader Types - Mobile First */
        .leader-types-grid {
            display: grid;
            grid-template-columns: 1fr; /* Single column on mobile */
            gap: 1rem;
            margin-top: 1.5rem;
        }

        .leader-type {
            background: white;
            padding: 1.5rem;
            border-radius: 8px;
            text-align: center;
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            border: 1px solid var(--steel-light);
        }

        .leader-type h3 {
            color: var(--text-primary);
            font-size: 1rem; /* 16px */
            font-weight: 700;
            margin-bottom: 0.75rem;
            line-height: 1.3;
        }

        .leader-type p {
            color: var(--text-secondary);
            font-size: 0.875rem; /* 14px */
            line-height: 1.5;
        }

        /* Facilitator Section - Mobile First */
        .facilitator-section {
            background: white;
        }

        .facilitator-content {
            display: grid;
            grid-template-columns: 1fr; /* Single column on mobile */
            gap: 1.5rem;
            align-items: center;
            margin-top: 1.5rem;
            text-align: center;
        }

        .facilitator-photo {
            text-align: center;
        }

        .photo-placeholder {
            width: 120px; /* Smaller on mobile */
            height: 120px;
            border-radius: 50%;
            background: var(--steel-light);
            border: 3px solid var(--ember-fire);
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1rem auto;
            color: var(--text-secondary);
            font-size: 0.75rem; /* 12px */
            text-align: center;
            padding: 1rem;
            background-image: url('https://lh3.googleusercontent.com/d/1NCpgRLwdlqJ-T3roL9XRZlhqVGS1ea4h');
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
        }

        .facilitator-bio {
            font-size: 0.9rem; /* 14px */
            color: var(--text-secondary);
            line-height: 1.6;
            margin-bottom: 1.5rem;
        }

        .testimonial {
            background: var(--warm);
            padding: 1rem; /* Reduced for mobile */
            border-radius: 8px;
            border-left: 4px solid var(--ember-fire);
            border-top: 1px solid var(--steel-light);
            margin-bottom: 1rem;
            text-align: left;
        }

        .testimonial-text {
            font-style: italic;
            color: var(--text-secondary);
            margin-bottom: 0.5rem;
            font-size: 0.875rem; /* 14px */
            line-height: 1.5;
        }

        .testimonial-author {
            font-weight: 700;
            color: var(--ember-fire);
            font-size: 0.875rem; /* 14px */
        }

        /* Program Details - Mobile First */
        .program-grid {
            display: grid;
            grid-template-columns: 1fr; /* Single column on mobile */
            gap: 1rem;
            margin-top: 1.5rem;
        }

        .program-detail {
            text-align: center;
            background: white;
            padding: 1.5rem 1rem;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            border: 1px solid var(--steel-light);
            border-top: 3px solid var(--steel-medium);
        }

        .detail-number {
            font-size: 1.25rem; /* 20px */
            font-weight: 800;
            color: var(--ember-fire);
            display: block;
            margin-bottom: 0.5rem;
        }

        .detail-label {
            font-weight: 600;
            margin-bottom: 0.5rem;
            color: var(--text-primary);
            font-size: 0.875rem; /* 14px */
        }

        .detail-description {
            color: var(--text-secondary);
            font-size: 0.75rem; /* 12px */
            line-height: 1.4;
        }

        /* Outcomes - Mobile First */
        .outcomes-grid {
            display: grid;
            grid-template-columns: 1fr; /* Single column on mobile */
            gap: 1rem;
            margin-top: 1.5rem;
        }

        .outcome-item {
            background: rgba(255,255,255,0.95);
            backdrop-filter: blur(10px);
            padding: 1.5rem;
            border-radius: 8px;
            border-left: 4px solid var(--ember-fire);
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            text-align: left;
        }

        .outcome-title {
            font-weight: 700;
            color: var(--ember-fire);
            margin-bottom: 0.5rem;
            font-size: 0.875rem; /* 14px */
            line-height: 1.3;
            text-align: left;
        }

        .outcome-description {
            color: var(--text-secondary);
            font-size: 0.8125rem; /* 13px */
            line-height: 1.5;
            text-align: left;
        }

        /* Investment Section */
        .investment {
            background: var(--forge-gradient);
            text-align: center;
            color: white;
            position: relative;
        }

        .investment::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.1);
            z-index: 1;
        }

        .investment .container {
            position: relative;
            z-index: 2;
        }

        .investment .section-title {
            color: white;
        }

        .investment-card {
            background: rgba(255,255,255,0.95);
            border: 2px solid rgba(255,255,255,0.3);
            border-radius: 8px;
            padding: 1.5rem;
            max-width: 100%;
            margin: 1.5rem auto;
            backdrop-filter: blur(10px);
            color: var(--text-primary);
        }

        .price {
            font-size: 1.75rem; /* 28px - smaller on mobile */
            font-weight: 800;
            color: var(--ember-fire);
            margin-bottom: 0.5rem;
        }

        /* FAQ - Mobile First with left alignment */
        .faq-item {
            background: white;
            border-radius: 8px;
            margin-bottom: 1rem;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .faq-content {
            padding: 1.5rem;
            border-left: 4px solid var(--ember-fire);
            text-align: left;
        }

        .faq-question {
            color: var(--ember-fire);
            font-weight: 700;
            margin-bottom: 0.5rem;
            font-size: 0.875rem; /* 14px */
            text-align: left;
            line-height: 1.3;
        }

        .faq-answer {
            color: var(--text-secondary);
            line-height: 1.6;
            font-size: 0.875rem; /* 14px */
            text-align: left;
        }

        /* Application Process - Mobile First */
        .application-section {
            background: var(--warm);
            color: var(--text-primary);
            text-align: center;
            border-top: 4px solid var(--steel-light);
        }

        .process-steps {
            display: flex;
            flex-direction: column; /* Stacked on mobile */
            align-items: center;
            gap: 1.5rem;
            margin: 2rem 0;
            list-style: none;
            padding: 0;
        }

        .process-step {
            display: flex;
            align-items: center;
            gap: 1rem;
            max-width: 280px;
            text-align: left;
            width: 100%;
        }

        .step-number {
            width: 44px; /* WCAG minimum touch target */
            height: 44px;
            background: var(--ember-fire);
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 800;
            font-size: 1.125rem; /* 18px */
            flex-shrink: 0;
            line-height: 1;
            text-align: center;
            font-family: 'Inter', system-ui, sans-serif;
        }

        .step-content {
            flex: 1;
        }

        .step-title {
            font-weight: 700;
            margin-bottom: 0.25rem;
            color: var(--text-primary);
            font-size: 0.875rem; /* 14px */
        }

        .step-description {
            font-size: 0.8125rem; /* 13px */
            color: var(--text-secondary);
            line-height: 1.4;
        }

        /* Tablet Styles - 640px and up */
        @media (min-width: 640px) {
            .container {
                max-width: 640px;
                padding: 0 1.5rem;
            }

            .hero {
                padding: 3rem 0;
                min-height: 70vh;
            }

            .hero h1 {
                font-size: 2.25rem; /* 36px */
            }

            .hero-subtitle {
                font-size: 1.125rem; /* 18px */
                max-width: 600px;
            }

            .cohort-details {
                grid-template-columns: repeat(3, 1fr);
                max-width: 500px;
                padding: 1.5rem;
            }

            .section-title {
                font-size: 1.875rem; /* 30px */
            }

            .section-subtitle {
                font-size: 1.125rem; /* 18px */
                max-width: 600px;
            }

            .competencies-grid {
                grid-template-columns: repeat(2, 1fr);
                gap: 1.5rem;
            }

            .leader-types-grid {
                grid-template-columns: repeat(2, 1fr);
                gap: 1.5rem;
            }

            .photo-placeholder {
                width: 150px;
                height: 150px;
            }

            .program-grid {
                grid-template-columns: repeat(2, 1fr);
                gap: 1.5rem;
            }

            .outcomes-grid {
                grid-template-columns: repeat(2, 1fr);
                gap: 1.5rem;
            }

            .investment-card {
                max-width: 500px;
                padding: 2rem;
            }

            .price {
                font-size: 2.25rem; /* 36px */
            }

            section {
                padding: 3rem 0;
            }
        }

        /* Desktop Styles - 1024px and up */
        @media (min-width: 1024px) {
            .container {
                max-width: 1200px;
                padding: 0 2rem;
            }

            .hero {
                padding: 4rem 0;
                min-height: 80vh;
            }

            .hero h1 {
                font-size: clamp(2.5rem, 4vw, 3.5rem);
            }

            .hero-subtitle {
                font-size: clamp(1.125rem, 2vw, 1.25rem);
            }

            .section-title {
                font-size: 2.25rem; /* 36px */
            }

            .competencies-grid {
                grid-template-columns: repeat(3, 1fr);
                gap: 2rem;
            }

            .leader-types-grid {
                grid-template-columns: repeat(3, 1fr);
                gap: 2rem;
            }

            .facilitator-content {
                grid-template-columns: 1fr 2fr;
                text-align: left;
                gap: 3rem;
            }

            .facilitator-photo {
                text-align: center;
            }

            .photo-placeholder {
                width: 180px;
                height: 180px;
            }

            .program-grid {
                grid-template-columns: repeat(4, 1fr);
                gap: 2rem;
            }

            .outcomes-grid {
                grid-template-columns: repeat(3, 1fr);
                gap: 2rem;
            }

            .process-steps {
                flex-direction: row;
                justify-content: center;
                gap: 2rem;
                align-items: flex-start;
            }

            .process-step {
                flex-direction: column;
                text-align: center;
                max-width: 120px;
                align-items: center;
            }

            .step-content {
                margin-top: 0.5rem;
                min-height: 60px;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
            }

            section {
                padding: 4rem 0;
            }
        }

        /* High Contrast Mode Support - WCAG AAA */
        @media (prefers-contrast: high) {
            :root {
                --text-primary: #000000;
                --text-secondary: #000000;
                --link-color: #0000FF;
                --link-hover: #0000CC;
            }
            
            .hero::before,
            .investment::before {
                background: rgba(0,0,0,0.8);
            }
            
            .competency-card,
            .leader-type,
            .program-detail,
            .outcome-item,
            .faq-item {
                border: 2px solid #000000;
            }
        }

        /* Reduced Motion Support - WCAG AAA */
        @media (prefers-reduced-motion: reduce) {
            *, *::before, *::after {
                animation-duration: 0.01ms !important;
                animation-iteration-count: 1 !important;
                transition-duration: 0.01ms !important;
                scroll-behavior: auto !important;
            }
        }

        /* Print Styles */
        @media print {
            .hero::before,
            .investment::before {
                display: none;
            }
            
            body {
                background: white !important;
                color: black !important;
            }
            
            .skip-nav,
            .cta-button {
                display: none;
            }
        }

        /* Large Desktop - 1440px and up */
        @media (min-width: 1440px) {
            .container {
                max-width: 1400px;
            }
        }

        /* Screen Reader Only Content */
        .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }
      `}</style>
      
      {/* Skip Navigation for WCAG AA Compliance */}
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      
      <Navigation />
      
      <main id="main-content" role="main" aria-label="The Crucible leadership program content">
        {/* HERO SECTION */}
        <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-warm via-white to-neutral-warm overflow-hidden pt-20" role="banner" aria-labelledby="hero-heading">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10 text-center">
            <h1 id="hero-heading" className="text-5xl sm:text-6xl md:text-7xl font-bold text-neutral-charcoal mb-6 leading-tight">
              Transform Your <span className="bg-ember-gradient bg-clip-text text-transparent">Impact</span>
            </h1>
            <p className="text-xl sm:text-2xl text-neutral-charcoal/80 mb-12 max-w-4xl mx-auto leading-relaxed">
              True leadership isn't about your title—it's about your capacity to create positive change and authentic influence
            </p>
            
            <div className="cohort-details" role="region" aria-label="Program details">
              <div className="cohort-detail">
                <div className="label">Cohort Starts</div>
                <div className="value">Oct 18</div>
              </div>
              <div className="cohort-detail">
                <div className="label">Duration</div>
                <div className="value">8 Weeks</div>
              </div>
              <div className="cohort-detail">
                <div className="label">Time/Week</div>
                <div className="value">3 Hours</div>
              </div>
            </div>

            <div className="urgency-banner" role="alert" aria-live="polite">
              Pilot Cohort $697 • Applications Close Oct 11 • Limited to 12 Members
            </div>
            
            <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '2rem', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto', lineHeight: '1.5' }}>
              <strong>The Challenge:</strong> Most leadership development fails because it ignores how leaders actually grow—through meaningful peer relationships and real challenges.
            </p>
            
            <a href="#application-section" className="cta-button" aria-label="Reserve your spot - scroll to application section">
              Reserve Your Spot
            </a>
          </div>
        </section>

        {/* THREE LEADERSHIP CAPACITIES */}
        <section className="competencies" aria-labelledby="competencies-heading">
          <div className="container">
            <h2 id="competencies-heading" className="section-title">Three Leadership Capacities That Change Everything</h2>
            <p className="section-subtitle">
              Research-validated capabilities that distinguish people who create positive influence and meaningful impact
            </p>
            
            <div className="competencies-grid" role="list" aria-label="Leadership competencies">
              <article className="competency-card" role="listitem">
                <h3 className="competency-title">Emotional Intelligence</h3>
                <p className="competency-description">
                  Master the inner and interpersonal dynamics that determine your capacity to influence others positively. Research shows EQ is a substantially stronger predictor of influence effectiveness than IQ or technical skills.
                </p>
                <div className="competency-outcomes">
                  <strong>You'll develop:</strong> The ability to navigate emotions during change, create psychological safety, build trust across differences, and maintain confidence through uncertainty.
                </div>
              </article>

              <article className="competency-card" role="listitem">
                <h3 className="competency-title">Personal Agency</h3>
                <p className="competency-description">
                  Develop unshakeable confidence in your ability to create positive change, regardless of your formal authority. Research shows that personal agency distinguishes those who thrive under uncertainty.
                </p>
                <div className="competency-outcomes">
                  <strong>You'll develop:</strong> The capacity to drive initiatives without formal authority, create innovation within established structures, and develop solutions for unprecedented situations.
                </div>
              </article>

              <article className="competency-card" role="listitem">
                <h3 className="competency-title">Authentic Connection</h3>
                <p className="competency-description">
                  Build the deep relationships that amplify your positive impact. Research confirms authentic behaviors drive higher engagement and performance in any context where humans work together.
                </p>
                <div className="competency-outcomes">
                  <strong>You'll develop:</strong> The ability to build high-trust relationships, create psychological safety for honest dialogue, and develop partnerships that advance meaningful goals.
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* BUILT FOR LEADERS */}
        <section style={{ background: 'white', padding: '2rem 0' }} aria-labelledby="leaders-heading">
          <div className="container">
            <h2 id="leaders-heading" className="section-title" style={{ color: 'var(--ember-flame)' }}>Built for Leaders Across All Contexts</h2>
            <p className="section-subtitle">
              The Crucible brings together people from diverse backgrounds who share a commitment to developing their capacity for authentic influence and meaningful impact.
            </p>
            
            <div className="leader-types-grid" role="list" aria-label="Types of leaders">
              <article className="leader-type" role="listitem">
                <h3>Corporate Contributors</h3>
                <p>People working in organizations who want to develop their leadership capacity to create positive change and influence others authentically, regardless of title.</p>
              </article>

              <article className="leader-type" role="listitem">
                <h3>Social Impact Builders</h3>
                <p>Those working in nonprofits, foundations, and social organizations developing their leadership capacity to lead mission-driven work with sustainable impact.</p>
              </article>

              <article className="leader-type" role="listitem">
                <h3>Educational Innovators</h3>
                <p>Educators, administrators, and others in learning environments developing their leadership capacity to guide positive transformation.</p>
              </article>

              <article className="leader-type" role="listitem">
                <h3>Community Builders</h3>
                <p>Civic-minded individuals developing their leadership capacity to create positive change within their communities.</p>
              </article>

              <article className="leader-type" role="listitem">
                <h3>Creative Entrepreneurs</h3>
                <p>Business creators and innovators developing their leadership capacity to build ventures while maintaining authentic values.</p>
              </article>

              <article className="leader-type" role="listitem">
                <h3>Faith & Community Guides</h3>
                <p>Spiritual and community guides developing their leadership capacity to serve others while continuing their own growth journey.</p>
              </article>
            </div>
          </div>
        </section>

        {/* MATT'S FACILITATOR SECTION */}
        <section className="facilitator-section" aria-labelledby="facilitator-heading">
          <div className="container">
            <h2 id="facilitator-heading" className="section-title">Meet Your Facilitator</h2>
            <p className="section-subtitle">
              Expert in creating psychological safety and facilitating breakthrough conversations among leaders
            </p>
            
            <div className="facilitator-content">
              <div className="facilitator-photo">
                <div className="photo-placeholder" role="img" aria-label="Professional headshot of Matthew Callison, PhD, program facilitator">
                  <span className="sr-only">Photo of Matthew Callison, PhD</span>
                </div>
                <h3 style={{ color: 'var(--ember-fire)', fontSize: '1.125rem', fontWeight: 700, lineHeight: '1.3' }}>Matthew Callison, PhD</h3>
              </div>
              
              <div>
                <p className="facilitator-bio">
                  Matt brings two decades of experience in educational leadership, innovation, and transformation. His approach to leadership development is grounded in authentic peer connection rather than top-down expertise. Matt creates containers where leaders can challenge and support each other's growth through rigorous reflection and honest dialogue.
                </p>
                
                <blockquote className="testimonial">
                  <p className="testimonial-text">
                    Working with Matt has been a transformative experience due to his exceptional group facilitation skills. His ability to create an inclusive environment encourages open dialogue, allowing all voices to be heard. Matt masterfully navigates discussions, fostering collaboration and ensuring that each participant feels valued.
                  </p>
                  <cite className="testimonial-author">— Jeff Dorn</cite>
                </blockquote>

                <blockquote className="testimonial">
                  <p className="testimonial-text">
                    Matt's leadership in men's circles I've attended is both commanding and humble. His sincerity draws me in, and his ability to facilitate openings for authentic conversation is skillful. I always feel safe, seen, and heard.
                  </p>
                  <cite className="testimonial-author">— Mark Caddell</cite>
                </blockquote>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section style={{ background: 'var(--neutral-light)', padding: '2rem 0', borderTop: '4px solid var(--steel-light)' }} aria-labelledby="how-it-works-heading">
          <div className="container">
            <h2 id="how-it-works-heading" className="section-title" style={{ color: 'var(--ember-heat)' }}>How The Crucible Works</h2>
            <p className="section-subtitle">
              Each session follows a structured approach that maintains intensity while creating psychological safety for authentic leadership development
            </p>
            
            <div className="program-grid" role="list" aria-label="Program details">
              <article className="program-detail" role="listitem">
                <span className="detail-number">2h 15m</span>
                <div className="detail-label">Weekly Sessions</div>
                <p className="detail-description">
                  Structured peer learning with 8-12 leaders using proven frameworks
                </p>
              </article>

              <article className="program-detail" role="listitem">
                <span className="detail-number">45-60min</span>
                <div className="detail-label">Weekly Reflection</div>
                <p className="detail-description">
                  Personal implementation and practice of insights from your peer group
                </p>
              </article>

              <article className="program-detail" role="listitem">
                <span className="detail-number">100%</span>
                <div className="detail-label">Real Challenges</div>
                <p className="detail-description">
                  Work on actual current leadership challenges, not theoretical case studies
                </p>
              </article>

              <article className="program-detail" role="listitem">
                <span className="detail-number">Lifetime</span>
                <div className="detail-label">Peer Network</div>
                <p className="detail-description">
                  Build lasting relationships with leaders who support your ongoing growth
                </p>
              </article>
            </div>

            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', marginTop: '2rem', border: '1px solid var(--steel-light)' }}>
              <h3 style={{ color: 'var(--ember-fire)', marginBottom: '1.5rem', fontSize: '1.25rem', textAlign: 'center', lineHeight: '1.3', fontWeight: 'bold' }}>
                Session Structure: The Fire-Forged Process
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }} role="list" aria-label="Session structure">
                {/* Block 1: Opening the Circle */}
                <article style={{ background: 'var(--warm)', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--steel-light)' }} role="listitem">
                  <div style={{ background: 'var(--steel-medium)', color: 'white', padding: '0.75rem', textAlign: 'left' }}>
                    <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.125rem', lineHeight: '1.3' }}>Opening the Circle</h4>
                    <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>30 minutes</div>
                  </div>
                  <div style={{ padding: '0.75rem', textAlign: 'left' }}>
                    <div style={{ marginBottom: '0.25rem' }}>
                      <div style={{ borderLeft: '3px solid var(--steel-medium)', paddingLeft: '0.75rem', marginBottom: '0.375rem', textAlign: 'left' }}>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'left', display: 'block' }}>Flint</strong>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.125rem', margin: 0, textAlign: 'left', lineHeight: '1.4' }}>Purpose alignment and session intentions</p>
                      </div>
                      <div style={{ borderLeft: '3px solid var(--steel-medium)', paddingLeft: '0.75rem', textAlign: 'left' }}>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'left', display: 'block' }}>Hearth</strong>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.125rem', margin: 0, textAlign: 'left', lineHeight: '1.4' }}>Foundation setting and safety establishment</p>
                      </div>
                    </div>
                  </div>
                </article>

                {/* Block 2: Building Connection */}
                <article style={{ background: 'var(--warm)', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--steel-light)' }} role="listitem">
                  <div style={{ background: 'var(--ember-fire)', color: 'white', padding: '0.75rem', textAlign: 'left' }}>
                    <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.125rem', lineHeight: '1.3' }}>Building Connection</h4>
                    <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>45 minutes</div>
                  </div>
                  <div style={{ padding: '0.75rem', textAlign: 'left' }}>
                    <div style={{ marginBottom: '0.25rem' }}>
                      <div style={{ borderLeft: '3px solid var(--ember-fire)', paddingLeft: '0.75rem', marginBottom: '0.375rem', textAlign: 'left' }}>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'left', display: 'block' }}>Spark</strong>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.125rem', margin: 0, textAlign: 'left', lineHeight: '1.4' }}>Centering and preparation for engagement</p>
                      </div>
                      <div style={{ borderLeft: '3px solid var(--ember-fire)', paddingLeft: '0.75rem', marginBottom: '0.375rem', textAlign: 'left' }}>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'left', display: 'block' }}>Kindle</strong>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.125rem', margin: 0, textAlign: 'left', lineHeight: '1.4' }}>Structured sharing for trust development</p>
                      </div>
                      <div style={{ borderLeft: '3px solid var(--ember-fire)', paddingLeft: '0.75rem', textAlign: 'left' }}>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'left', display: 'block' }}>Fuel</strong>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.125rem', margin: 0, textAlign: 'left', lineHeight: '1.4' }}>Peer support and encouragement practices</p>
                      </div>
                    </div>
                  </div>
                </article>

                {/* Block 3: Transformation & Integration */}
                <article style={{ background: 'var(--warm)', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--steel-light)' }} role="listitem">
                  <div style={{ background: 'var(--flame-gradient)', color: 'white', padding: '0.75rem', textAlign: 'left' }}>
                    <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.125rem', lineHeight: '1.3' }}>Transformation & Integration</h4>
                    <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>60 minutes</div>
                  </div>
                  <div style={{ padding: '0.75rem', textAlign: 'left' }}>
                    <div style={{ marginBottom: '0.25rem' }}>
                      <div style={{ borderLeft: '3px solid var(--ember-flame)', paddingLeft: '0.75rem', marginBottom: '0.375rem', textAlign: 'left' }}>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'left', display: 'block' }}>Flame</strong>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.125rem', margin: 0, textAlign: 'left', lineHeight: '1.4' }}>Leadership insights and skill development</p>
                      </div>
                      <div style={{ borderLeft: '3px solid var(--ember-flame)', paddingLeft: '0.75rem', marginBottom: '0.375rem', textAlign: 'left' }}>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'left', display: 'block' }}>Forge</strong>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.125rem', margin: 0, textAlign: 'left', lineHeight: '1.4' }}>Collaborative problem-solving on real challenges</p>
                      </div>
                      <div style={{ borderLeft: '3px solid var(--ember-flame)', paddingLeft: '0.75rem', textAlign: 'left' }}>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'left', display: 'block' }}>Torch</strong>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.125rem', margin: 0, textAlign: 'left', lineHeight: '1.4' }}>Integration and commitment to specific actions</p>
                      </div>
                    </div>
                  </div>
                </article>
              </div>

              <div style={{ textAlign: 'center', marginTop: '1rem', fontStyle: 'italic', color: 'var(--text-secondary)', background: 'var(--neutral-light)', padding: '1rem', borderRadius: '8px', fontSize: '0.875rem' }}>
                This structure maintains intensity and focus while honoring systematic, respectful dialogue.
              </div>
            </div>

            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', marginTop: '1.5rem', textAlign: 'center', border: '1px solid var(--steel-light)' }}>
              <h3 style={{ color: 'var(--ember-fire)', marginBottom: '1rem', fontSize: '1.125rem', fontWeight: 'bold', lineHeight: '1.3' }}>
                Why This Cohort is Male-Only
              </h3>
              <p style={{ color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5, fontSize: '0.875rem' }}>
                Research shows that gender-focused cohorts create the psychological safety needed for authentic sharing about leadership challenges. When participants share similar contexts and experiences, learning accelerates and relationships deepen. Additional cohort options are being planned and will launch soon.
              </p>
            </div>
          </div>
        </section>

        {/* YOUR TRANSFORMATION */}
        <section style={{ background: 'var(--forge-gradient)', padding: '2rem 0', color: 'white', position: 'relative' }} aria-labelledby="transformation-heading">
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.2)', zIndex: 1 }}></div>
          <div className="container" style={{ position: 'relative', zIndex: 2 }}>
            <h2 id="transformation-heading" className="section-title" style={{ color: 'white' }}>Your Leadership Transformation</h2>
            <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.9)' }}>
              What you can expect after 8 weeks of intensive peer development focused on your capacity for influence and impact
            </p>
            
            <div className="outcomes-grid" role="list" aria-label="Program outcomes">
              <article className="outcome-item" role="listitem">
                <h3 className="outcome-title">Enhanced Emotional Intelligence</h3>
                <p className="outcome-description">Measurable improvement in EQ competencies through validated assessments, increasing your capacity to navigate complex relationships</p>
              </article>
              <article className="outcome-item" role="listitem">
                <h3 className="outcome-title">Increased Personal Agency</h3>
                <p className="outcome-description">Greater confidence in creating positive change and leading initiatives, regardless of your formal authority</p>
              </article>
              <article className="outcome-item" role="listitem">
                <h3 className="outcome-title">Stronger Authentic Connection</h3>
                <p className="outcome-description">Improved ability to build trust and develop meaningful relationships that support your values and goals</p>
              </article>
              <article className="outcome-item" role="listitem">
                <h3 className="outcome-title">Strategic Peer Network</h3>
                <p className="outcome-description">Lasting relationships with experienced peers who understand your journey and continue supporting your growth</p>
              </article>
              <article className="outcome-item" role="listitem">
                <h3 className="outcome-title">Real-World Application</h3>
                <p className="outcome-description">Immediate implementation of insights on your current challenges and opportunities for positive influence</p>
              </article>
              <article className="outcome-item" role="listitem">
                <h3 className="outcome-title">Continued Growth Tools</h3>
                <p className="outcome-description">Frameworks for ongoing leadership development throughout your life and across all your relationships</p>
              </article>
            </div>
          </div>
        </section>

        {/* INVESTMENT */}
        <section className="investment" aria-labelledby="investment-heading">
          <div className="container">
            <h2 id="investment-heading" className="section-title">Your Investment in Leadership Excellence</h2>
            
            <div className="investment-card">
              <div style={{ background: 'var(--ember-fire)', color: 'white', padding: '0.5rem 1rem', borderRadius: '20px', display: 'inline-block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem' }}>
                Pilot Cohort Pricing - Limited Time
              </div>
              <div className="price">$697</div>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                <span style={{ fontSize: '0.75rem' }}>Special pilot cohort pricing - regular price $2,997</span>
              </p>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>Complete 8-week transformation program</p>
              
              <div style={{ background: 'var(--warm)', padding: '1rem', borderRadius: '8px', margin: '1rem 0' }}>
                <div style={{ fontWeight: 700, marginBottom: '1rem', color: 'var(--ember-fire)', fontSize: '0.875rem' }}>Payment Structure:</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.75rem', textAlign: 'center' }}>
                  <div>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--ember-fire)' }}>$50</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>Application</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--ember-fire)' }}>$147</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>Upon Acceptance</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--ember-fire)' }}>$500</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>Before Start</div>
                  </div>
                </div>
              </div>
              
              <div style={{ background: 'var(--ember-fire)', color: 'white', padding: '1rem', borderRadius: '8px', marginTop: '1rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Your Investment is Protected</div>
                <p style={{ fontSize: '0.8125rem', margin: 0 }}>If you're not fully satisfied after Week 1, we'll provide a full refund.</p>
              </div>
            </div>

            <div style={{ background: 'white', border: '2px solid var(--ember-fire)', borderRadius: '8px', padding: '1.5rem', margin: '1.5rem auto', maxWidth: 400, textAlign: 'center' }}>
              <h3 style={{ color: 'var(--ember-fire)', fontWeight: 700, marginBottom: '1rem', fontSize: '1rem', lineHeight: '1.3' }}>Build Your Peer Network</h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.875rem' }}>Refer a colleague who gets accepted, and you each save $100.</p>
              <div style={{ background: 'var(--ember-fire)', color: 'white', padding: '0.5rem 1rem', borderRadius: '20px', display: 'inline-block', fontWeight: 600, fontSize: '0.8125rem' }}>
                Referral Bonus: $100 Each
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ background: 'white', padding: '2rem 0' }} aria-labelledby="faq-heading">
          <div className="container">
            <h2 id="faq-heading" className="section-title">Frequently Asked Questions</h2>
            
            <div style={{ maxWidth: '700px', margin: '0 auto' }} role="list" aria-label="Frequently asked questions">
              <article className="faq-item" role="listitem">
                <div className="faq-content">
                  <h3 className="faq-question">How is this different from executive coaching?</h3>
                  <p className="faq-answer">Executive coaching is typically one-on-one. The Crucible is peer-based learning where transformation happens through authentic connection with fellow leaders facing similar challenges. The facilitator creates the container—the real growth comes from the collective wisdom of the group.</p>
                </div>
              </article>

              <article className="faq-item" role="listitem">
                <div className="faq-content">
                  <h3 className="faq-question">What if I can't attend every session?</h3>
                  <p className="faq-answer">You are expected to attend every session. The intimate nature of peer learning requires consistent participation. One absence is accounted for, but more than that may result in removal from the program to preserve the integrity of the experience.</p>
                </div>
              </article>

              <article className="faq-item" role="listitem">
                <div className="faq-content">
                  <h3 className="faq-question">How do you ensure confidentiality?</h3>
                  <p className="faq-answer">All participants sign a comprehensive Non-Disclosure Agreement. What happens in The Crucible stays in The Crucible. This commitment allows leaders to be vulnerable about real challenges without concern for professional repercussions.</p>
                </div>
              </article>

              <article className="faq-item" role="listitem">
                <div className="faq-content">
                  <h3 className="faq-question">Is this virtual or in-person?</h3>
                  <p className="faq-answer">Sessions are conducted virtually via Zoom, making it accessible regardless of your location. This also enables us to bring together leaders from diverse industries and geographies for richer peer learning.</p>
                </div>
              </article>

              <article className="faq-item" role="listitem">
                <div className="faq-content">
                  <h3 className="faq-question">Is this appropriate for my situation?</h3>
                  <p className="faq-answer">The Crucible is designed for anyone with a genuine desire to develop their leadership capacity - regardless of title or formal authority. What matters is your readiness for authentic growth and willingness to engage with peers who take developing their influence seriously.</p>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* APPLICATION PROCESS */}
        <section id="application-section" className="application-section" aria-labelledby="application-heading">
          <div className="container">
            <h2 id="application-heading" style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text-primary)', lineHeight: '1.3' }}>Start Your Application</h2>
            <p style={{ fontSize: '1rem', marginBottom: '1.5rem', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              The Crucible is selective by design. Our application process ensures the right fit for transformational peer learning.
            </p>
            
            <div style={{ background: 'white', border: '2px solid var(--ember-fire)', borderRadius: '8px', padding: '1rem', display: 'inline-block', marginBottom: '1.5rem' }}>
              <div style={{ fontWeight: 700, color: 'var(--ember-fire)', fontSize: '0.875rem' }}>Pilot cohort pricing $697 ends when we reach 12 members</div>
            </div>
            
            <ol className="process-steps" aria-label="Five-step application process">
              <li className="process-step">
                <div className="step-number" aria-label="Step 1">1</div>
                <div className="step-content">
                  <div className="step-title">Apply</div>
                  <p className="step-description">Written application + $50 fee</p>
                </div>
              </li>
              <li className="process-step">
                <div className="step-number" aria-label="Step 2">2</div>
                <div className="step-content">
                  <div className="step-title">Review</div>
                  <p className="step-description">48 hours</p>
                </div>
              </li>
              <li className="process-step">
                <div className="step-number" aria-label="Step 3">3</div>
                <div className="step-content">
                  <div className="step-title">Interview</div>
                  <p className="step-description">30-minute conversation</p>
                </div>
              </li>
              <li className="process-step">
                <div className="step-number" aria-label="Step 4">4</div>
                <div className="step-content">
                  <div className="step-title">Commit</div>
                  <p className="step-description">$147 deposit</p>
                </div>
              </li>
              <li className="process-step">
                <div className="step-number" aria-label="Step 5">5</div>
                <div className="step-content">
                  <div className="step-title">Transform</div>
                  <p className="step-description">8 weeks of growth</p>
                </div>
              </li>
            </ol>

            <a 
              href="https://form.typeform.com/to/v7C6ULZm" 
              target="_blank" 
              rel="noopener noreferrer"
              className="cta-button"
              style={{ 
                fontSize: '1rem', 
                padding: '12px 24px',
                marginBottom: '1.5rem',
                display: 'inline-block'
              }}
              aria-label="Start your application - opens Typeform in new window"
            >
              Start Your Application
            </a>

            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Questions about the process?{' '}
              <a 
                href="https://calendly.com/matt-emberimpact/30min" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: 'var(--link-color)', textDecoration: 'underline', fontWeight: 600 }}
                aria-label="Schedule a discovery call with Matt - opens Calendly in new window"
              >
                Schedule a discovery call with Matt
              </a>
            </p>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default CruciblePage;